import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { api, BRAND_LABELS, fmtRM } from "@/lib/api";

const TABS = ["Models", "Offers", "Locations", "Leads"];
const EMPTY_MODEL = {
  brand: "honda", name: "", tagline: "", body_type: "Sedan", price_from: 0, image: "",
  official_url: "",
  featured: false, badge: "",
  specs: { engine: "", power: "", torque: "", transmission: "", fuel_economy: "", seats: "5", warranty: "" },
  variants: [], features: [],
};
const EMPTY_OFFER = { brand: "all", title: "", highlight: "", description: "", points: [], valid_until: "", terms: "", active: true };

const inputCls = "w-full border border-black/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-black";
const labelCls = "mb-1.5 block font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500";

const Field = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

export default function Admin() {
  const [tab, setTab] = useState("Models");
  const [models, setModels] = useState([]);
  const [offers, setOffers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null); // {kind:'model'|'offer', data, id?}
  const [variantsText, setVariantsText] = useState("");
  const [featuresText, setFeaturesText] = useState("");

  const load = () => {
    api.models().then(setModels).catch(() => toast.error("Failed to load models"));
    api.offers({ all: true }).then(setOffers).catch(() => {});
    api.locations().then(setLocations).catch(() => {});
    api.enquiries().then(setLeads).catch(() => {});
  };

  useEffect(() => {
    document.title = "AutoWorld Staff — Content Manager";
    load();
  }, []);

  const openModel = (m) => {
    const data = m ? { ...EMPTY_MODEL, ...m, specs: { ...EMPTY_MODEL.specs, ...m.specs } } : { ...EMPTY_MODEL };
    setVariantsText((data.variants || []).map((v) => `${v.name} | ${v.price}`).join("\n"));
    setFeaturesText((data.features || []).join("\n"));
    setEditing({ kind: "model", data, id: m?.id });
  };

  const saveModel = async () => {
    const d = editing.data;
    if (!d.name.trim()) return toast.error("Model name is required");
    const payload = {
      ...d,
      price_from: Number(d.price_from) || 0,
      variants: variantsText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
        const [name, price] = l.split("|").map((s) => s.trim());
        return { name, price: Number(price) || 0 };
      }),
      features: featuresText.split("\n").map((l) => l.trim()).filter(Boolean),
    };
    try {
      if (editing.id) await api.updateModel(editing.id, payload);
      else await api.createModel(payload);
      toast.success("Model saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const saveOffer = async () => {
    const d = editing.data;
    if (!d.title.trim()) return toast.error("Offer title is required");
    try {
      if (editing.id) await api.updateOffer(editing.id, d);
      else await api.createOffer(d);
      toast.success("Offer saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const saveLocation = async (loc) => {
    try {
      await api.updateLocation(loc.id, loc);
      toast.success("Location updated");
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 pt-16 md:pt-20" data-testid="admin-page">
      <div className="mx-auto max-w-[1200px] px-5 py-10 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">AutoWorld Staff · No login required</div>
            <h1 className="mt-2 font-display text-4xl font-extrabold uppercase tracking-tighter">Content Manager</h1>
          </div>
          {tab === "Models" && (
            <button data-testid="admin-add-model" onClick={() => openModel(null)} className="flex items-center gap-2 bg-black px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-neutral-800">
              <Plus className="h-4 w-4" /> Add Model
            </button>
          )}
          {tab === "Offers" && (
            <button data-testid="admin-add-offer" onClick={() => setEditing({ kind: "offer", data: { ...EMPTY_OFFER } })} className="flex items-center gap-2 bg-black px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-neutral-800">
              <Plus className="h-4 w-4" /> Add Offer
            </button>
          )}
        </div>

        <div className="mt-8 flex gap-2 border-b border-black/10" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              className={`px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                tab === t ? "bg-black text-white" : "text-neutral-500 hover:text-black"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* MODELS TAB */}
        {tab === "Models" && (
          <div className="mt-6 divide-y divide-black/10 border border-black/10 bg-white" data-testid="admin-models-list">
            {models.map((m) => (
              <div key={m.id} className="flex flex-wrap items-center gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-bold">{BRAND_LABELS[m.brand]} {m.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    {m.body_type} · {fmtRM(m.price_from)} {m.featured ? "· Featured" : ""}
                  </div>
                </div>
                <button data-testid={`admin-edit-model-${m.id}`} onClick={() => openModel(m)} className="flex items-center gap-1.5 border border-black px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-black hover:text-white">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  data-testid={`admin-delete-model-${m.id}`}
                  onClick={async () => { if (window.confirm(`Delete ${m.name}?`)) { await api.deleteModel(m.id); toast.success("Deleted"); load(); } }}
                  className="flex h-9 w-9 items-center justify-center border border-black/20 text-neutral-500 hover:border-black hover:text-black"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {models.length === 0 && <p className="p-8 text-center text-sm text-neutral-500">No models yet — add your first one.</p>}
          </div>
        )}

        {/* OFFERS TAB */}
        {tab === "Offers" && (
          <div className="mt-6 divide-y divide-black/10 border border-black/10 bg-white" data-testid="admin-offers-list">
            {offers.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-bold">{o.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    {BRAND_LABELS[o.brand] || o.brand} · {o.highlight} · until {o.valid_until} ·{" "}
                    <span className={o.active ? "text-black" : "text-neutral-400 line-through"}>{o.active ? "Live" : "Hidden"}</span>
                  </div>
                </div>
                <button data-testid={`admin-edit-offer-${o.id}`} onClick={() => setEditing({ kind: "offer", data: { ...o }, id: o.id })} className="flex items-center gap-1.5 border border-black px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-black hover:text-white">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  data-testid={`admin-delete-offer-${o.id}`}
                  onClick={async () => { if (window.confirm(`Delete "${o.title}"?`)) { await api.deleteOffer(o.id); toast.success("Deleted"); load(); } }}
                  className="flex h-9 w-9 items-center justify-center border border-black/20 text-neutral-500 hover:border-black hover:text-black"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {offers.length === 0 && <p className="p-8 text-center text-sm text-neutral-500">No offers yet — add this month's deals.</p>}
          </div>
        )}

        {/* LOCATIONS TAB */}
        {tab === "Locations" && (
          <div className="mt-6 grid gap-6 md:grid-cols-3" data-testid="admin-locations-list">
            {locations.map((l, i) => (
              <LocationEditor key={l.id} loc={l} index={i} onSave={saveLocation} />
            ))}
          </div>
        )}

        {/* LEADS TAB */}
        {tab === "Leads" && (
          <div className="mt-6 divide-y divide-black/10 border border-black/10 bg-white" data-testid="admin-leads-list">
            {leads.map((e) => (
              <div key={e.id} className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-display font-bold">{e.name} · {e.phone}</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">
                    {e.type} · {e.interest} · {new Date(e.created_at).toLocaleDateString("en-MY")}
                  </div>
                </div>
                {e.message && <p className="mt-2 whitespace-pre-line text-sm text-neutral-600">{e.message}</p>}
              </div>
            ))}
            {leads.length === 0 && <p className="p-8 text-center text-sm text-neutral-500">No enquiries yet.</p>}
          </div>
        )}
      </div>

      {/* EDITOR MODAL */}
      {editing && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 backdrop-blur-sm md:items-center" onClick={() => setEditing(null)} data-testid="admin-editor-modal">
          <div data-lenis-prevent className="aw-scroll max-h-[92vh] w-full max-w-2xl overflow-y-auto bg-white p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
                {editing.id ? "Edit" : "Add"} {editing.kind === "model" ? "Model" : "Offer"}
              </h2>
              <button data-testid="admin-editor-close" onClick={() => setEditing(null)} aria-label="Close" className="flex h-10 w-10 items-center justify-center border border-black/20 hover:bg-black hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            {editing.kind === "model" ? (
              <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="admin-model-form">
                <Field label="Brand">
                  <select className={inputCls} data-testid="model-form-brand" value={editing.data.brand} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, brand: e.target.value } })}>
                    {["honda", "proton", "jaecoo", "omoda"].map((b) => <option key={b} value={b}>{BRAND_LABELS[b]}</option>)}
                  </select>
                </Field>
                <Field label="Model name">
                  <input className={inputCls} data-testid="model-form-name" value={editing.data.name} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })} />
                </Field>
                <Field label="Tagline">
                  <input className={inputCls} data-testid="model-form-tagline" value={editing.data.tagline} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, tagline: e.target.value } })} />
                </Field>
                <Field label="Body type">
                  <select className={inputCls} data-testid="model-form-body" value={editing.data.body_type} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, body_type: e.target.value } })}>
                    {["Sedan", "SUV", "Hatchback", "MPV", "EV"].map((b) => <option key={b}>{b}</option>)}
                  </select>
                </Field>
                <Field label="Starting price (RM)">
                  <input type="number" className={inputCls} data-testid="model-form-price" value={editing.data.price_from} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, price_from: e.target.value } })} />
                </Field>
                <Field label="Badge (optional)">
                  <input className={inputCls} data-testid="model-form-badge" placeholder="e.g. NEW 2026" value={editing.data.badge} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, badge: e.target.value } })} />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Image URL">
                    <input className={inputCls} data-testid="model-form-image" placeholder="https://…" value={editing.data.image} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, image: e.target.value } })} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Official brand page link (specs/details open there — e.g. https://www.honda.com.my/model/city)">
                    <input className={inputCls} data-testid="model-form-official-url" placeholder="https://…" value={editing.data.official_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, official_url: e.target.value } })} />
                  </Field>
                </div>
                {["engine", "power", "torque", "transmission", "fuel_economy", "seats", "warranty"].map((k) => (
                  <Field key={k} label={k.replace("_", " ")}>
                    <input
                      className={inputCls}
                      data-testid={`model-form-spec-${k}`}
                      value={editing.data.specs[k] || ""}
                      onChange={(e) => setEditing({ ...editing, data: { ...editing.data, specs: { ...editing.data.specs, [k]: e.target.value } } })}
                    />
                  </Field>
                ))}
                <Field label="Featured on homepage">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="model-form-featured" checked={editing.data.featured} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, featured: e.target.checked } })} className="h-4 w-4 accent-black" />
                    Show in Featured Models
                  </label>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Variants — one per line: Name | Price">
                    <textarea rows={3} className={inputCls} data-testid="model-form-variants" placeholder={"1.5 S | 84900\n1.5 V | 94900"} value={variantsText} onChange={(e) => setVariantsText(e.target.value)} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Features — one per line">
                    <textarea rows={3} className={inputCls} data-testid="model-form-features" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} />
                  </Field>
                </div>
                <button data-testid="model-form-save" onClick={saveModel} className="mt-2 bg-black py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-white hover:bg-neutral-800 sm:col-span-2">
                  Save Model
                </button>
              </div>
            ) : (
              <div className="mt-6 grid gap-4" data-testid="admin-offer-form">
                <Field label="Brand">
                  <select className={inputCls} data-testid="offer-form-brand" value={editing.data.brand} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, brand: e.target.value } })}>
                    {["all", "honda", "proton", "jaecoo", "omoda"].map((b) => <option key={b} value={b}>{BRAND_LABELS[b]}</option>)}
                  </select>
                </Field>
                <Field label="Title">
                  <input className={inputCls} data-testid="offer-form-title" value={editing.data.title} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} />
                </Field>
                <Field label="Highlight (big text)">
                  <input className={inputCls} data-testid="offer-form-highlight" placeholder="e.g. RM5,000 REBATE" value={editing.data.highlight} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, highlight: e.target.value } })} />
                </Field>
                <Field label="Description">
                  <textarea rows={3} className={inputCls} data-testid="offer-form-description" value={editing.data.description} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} />
                </Field>
                <Field label="Bullet points (one per line — shown instead of description)">
                  <textarea rows={4} className={inputCls} data-testid="offer-form-points" placeholder={"E — RM8,000 + RM2,000 H2H\nV — RM19,000"} value={(editing.data.points || []).join("\n")} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, points: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) } })} />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Valid until">
                    <input className={inputCls} data-testid="offer-form-valid" placeholder="31 July 2026" value={editing.data.valid_until} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, valid_until: e.target.value } })} />
                  </Field>
                  <Field label="Terms">
                    <input className={inputCls} data-testid="offer-form-terms" value={editing.data.terms} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, terms: e.target.value } })} />
                  </Field>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="offer-form-active" checked={editing.data.active} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, active: e.target.checked } })} className="h-4 w-4 accent-black" />
                  Live on website
                </label>
                <button data-testid="offer-form-save" onClick={saveOffer} className="mt-2 bg-black py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-white hover:bg-neutral-800">
                  Save Offer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LocationEditor({ loc, index, onSave }) {
  const [data, setData] = useState(loc);
  useEffect(() => setData(loc), [loc]);
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  return (
    <div className="border border-black/10 bg-white p-5" data-testid={`admin-location-${index}`}>
      <div className="font-display text-lg font-bold">{data.name}</div>
      <div className="mt-4 space-y-3">
        <Field label="Address"><input className={inputCls} data-testid={`location-address-${index}`} value={data.address} onChange={set("address")} /></Field>
        <Field label="City / Postcode"><input className={inputCls} data-testid={`location-city-${index}`} value={data.city} onChange={set("city")} /></Field>
        <Field label="Hours"><input className={inputCls} data-testid={`location-hours-${index}`} value={data.hours} onChange={set("hours")} /></Field>
        <Field label="Phone"><input className={inputCls} data-testid={`location-phone-${index}`} value={data.phone} onChange={set("phone")} /></Field>
        <Field label="Google Maps search term"><input className={inputCls} data-testid={`location-map-${index}`} value={data.map_query} onChange={set("map_query")} /></Field>
        <button data-testid={`location-save-${index}`} onClick={() => onSave(data)} className="w-full bg-black py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white hover:bg-neutral-800">
          Save Location
        </button>
      </div>
    </div>
  );
}
